import React, { useState, useEffect } from 'react'
import {
  Box,
  IconButton,
  Badge,
  Popover,
  List,
  Typography,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
} from '@material-ui/core'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import CancelIcon from '@mui/icons-material/Cancel'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import WarningIcon from '@mui/icons-material/Warning'
import InfoIcon from '@mui/icons-material/Info'
import TimerIcon from '@mui/icons-material/Timer'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { alpha, styled } from '@material-ui/core/styles'
import { useUploadTrackerStore } from '../../zustand/uploadStore'
import uploadService from '../../services/UploadService'
import { useSnackbar } from 'notistack5'

// Modern styling for the badge
const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    transition: 'all 0.2s ease',
  },
}))

// Enhanced styling for the upload icon container
const IconContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
  },
}))

// Styled warning indicator for interrupted uploads
const WarningIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -4,
  left: -4,
  backgroundColor: theme.palette.warning.main,
  borderRadius: '50%',
  width: 10,
  height: 10,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: `1px solid ${theme.palette.warning.dark}`,
  boxShadow: `0 0 0 2px ${alpha(theme.palette.warning.main, 0.2)}`,
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': {
      boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0.4)}`,
    },
    '70%': {
      boxShadow: `0 0 0 6px ${alpha(theme.palette.warning.main, 0)}`,
    },
    '100%': {
      boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0)}`,
    },
  },
}))

// Custom styled tab for status filtering
const StyledTab = styled(Tab)(({ theme }) => ({
  minWidth: 'auto',
  padding: theme.spacing(1, 1.5),
  fontSize: '0.8125rem',
  minHeight: 40,
  fontWeight: 500,
  textTransform: 'none',
  '&.MuiTab-root': {
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0, 0.5),
  },
}))

// Styled upload item base component
const UploadItemBase = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0.75, 0),
  position: 'relative',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.lighter, 0.1),
  },
}))

// Styled component for active uploads
const ActiveUploadItem = styled(UploadItemBase)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.lighter, 0.05),
}))

// Styled component for interrupted uploads
const InterruptedUploadItem = styled(UploadItemBase)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.warning.lighter, 0.1),
  borderLeft: `3px solid ${theme.palette.warning.main}`,
  '&:hover': {
    backgroundColor: alpha(theme.palette.warning.lighter, 0.2),
  },
}))

// Styled component for paused uploads
const PausedUploadItem = styled(UploadItemBase)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.info.lighter, 0.1),
  borderLeft: `3px solid ${theme.palette.info.main}`,
  '&:hover': {
    backgroundColor: alpha(theme.palette.info.lighter, 0.2),
  },
}))

// Styled component for completed uploads
const CompletedUploadItem = styled(UploadItemBase)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.success.lighter, 0.05),
  '&:hover': {
    backgroundColor: alpha(theme.palette.success.lighter, 0.1),
  },
}))

// Styled component for failed uploads
const FailedUploadItem = styled(UploadItemBase)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.error.lighter, 0.05),
  '&:hover': {
    backgroundColor: alpha(theme.palette.error.lighter, 0.1),
  },
}))

// Styled progress bar
const ProgressBar = styled(Box)(({ theme, value = 0 }) => ({
  height: 4,
  borderRadius: 2,
  backgroundColor: alpha(theme.palette.primary.lighter, 0.3),
  position: 'relative',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: `${value}%`,
    height: '100%',
    backgroundColor: theme.palette.primary.main,
    transition: 'width 0.3s ease',
    borderRadius: 2,
  },
}))

// Styled progress bar for interrupted uploads
const InterruptedProgressBar = styled(ProgressBar)(({ theme }) => ({
  '&::after': {
    backgroundColor: theme.palette.warning.main,
  },
}))

// Styled progress bar for paused uploads
const PausedProgressBar = styled(ProgressBar)(({ theme }) => ({
  '&::after': {
    backgroundColor: theme.palette.info.main,
  },
}))

// TabPanel component for the tabbed interface
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`upload-tabpanel-${index}`}
      aria-labelledby={`upload-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  )
}

// Tab status values
const TAB_ALL = 0
const TAB_ACTIVE = 1
const TAB_INTERRUPTED = 2

const ACCEPT_PATTERNS = {
  ont_bam: '.bam',
  pacbio_bam: '.bam',
  fastq: '.fastq,.fq,.fastq.gz,.fq.gz',
  vcf: '.vcf,.vcf.gz',
  bed: '.bed',
  gene_panel: '.txt,.csv',
}

const getAcceptPattern = (metadata) => {
  if (!metadata) return '*'
  const pattern = ACCEPT_PATTERNS[metadata.fileCategory]
  if (pattern) return pattern
  return metadata.filetype || '*'
}

const UploadTracker = () => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [fileInputKey, setFileInputKey] = useState(Date.now()) // For resetting the file input
  const [mismatchDialog, setMismatchDialog] = useState({
    open: false,
    details: null,
    uploadToResume: null,
  })
  const [tabValue, setTabValue] = useState(TAB_ALL)
  const { enqueueSnackbar } = useSnackbar()
  const [isClearInterruptedDialogOpen, setIsClearInterruptedDialogOpen] = useState(false)

  // Get state and actions from the store
  const uploads = useUploadTrackerStore((state) => state.uploads)
  const removeUpload = useUploadTrackerStore((state) => state.removeUpload)
  const completedUploads = useUploadTrackerStore((state) => state.completedUploads)
  const failedUploads = useUploadTrackerStore((state) => state.failedUploads)
  const activeUploads = useUploadTrackerStore((state) => state.activeUploads)
  const interruptedUploads = useUploadTrackerStore((state) => state.interruptedUploads)
  const pausedUploads = useUploadTrackerStore((state) => state.pausedUploads)
  const totalUploads = useUploadTrackerStore((state) => state.totalUploads)

  // Get interrupted uploads as array for button logic
  const interruptedUploadsArray = Object.values(uploads).filter((u) => u.status === 'interrupted')

  useEffect(() => {
    // Initialize the upload service
    uploadService.init()
  }, [])

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleRemove = (id) => {
    removeUpload(id)
  }

  const handleCancel = (id) => {
    // Use the service to cancel the upload
    uploadService.cancelUpload(id)
  }

  const handlePause = (id) => {
    // Use the service to pause the upload
    uploadService.pauseUpload(id)
  }

  const handleResumePaused = (id) => {
    // Use the service to resume the paused upload
    uploadService.resumePausedUpload(id)
  }

  // Handle file mismatch
  const handleFileMismatch = (validationResult, uploadToResume) => {
    setMismatchDialog({
      open: true,
      details: validationResult,
      uploadToResume,
    })
  }

  // Handle force proceeding with a mismatched file
  const handleForceProceed = () => {
    const { uploadToResume } = mismatchDialog

    // Request a new file selection to proceed with force
    promptFileSelection(uploadToResume, true)

    setMismatchDialog({
      open: false,
      details: null,
      uploadToResume: null,
    })
  }

  // Handle cancelling a mismatch resume attempt
  const handleCancelMismatch = () => {
    setMismatchDialog({
      open: false,
      details: null,
      uploadToResume: null,
    })
  }

  // Prompt the user to select a file
  const promptFileSelection = (upload, forceProceed = false) => {
    // Trigger the file input dialog
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.style.display = 'none'
    fileInput.accept = getAcceptPattern(upload.metadata)
    fileInput.key = fileInputKey

    fileInput.onchange = (e) => {
      const file = e.target.files[0]
      if (file) {
        // Resume the upload with the newly selected file
        uploadService.startUpload(file, {
          metadata: upload.metadata,
          resuming: true,
          uploadToResume: upload,
          forceProceed: forceProceed,
          onMismatch: (validationResult) => handleFileMismatch(validationResult, upload),
        })
      }
      // Reset the key to allow selecting the same file again
      setFileInputKey(Date.now())
    }

    document.body.appendChild(fileInput)
    fileInput.click()
    document.body.removeChild(fileInput)
  }

  // Prompt the user to select a file to resume an interrupted upload
  const handleResumeUpload = (upload) => {
    promptFileSelection(upload)
  }

  const formatTimeRemaining = (seconds) => {
    if (!seconds || seconds <= 0) return 'Calculating...'
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    return `${minutes}m ${seconds % 60}s`
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  // Filter uploads based on the selected tab
  const filteredUploads = Object.values(uploads).filter((upload) => {
    if (tabValue === TAB_ALL) return true
    if (tabValue === TAB_ACTIVE) return upload.status === 'uploading' || upload.status === 'paused'
    if (tabValue === TAB_INTERRUPTED) return upload.status === 'interrupted'
    return true
  })

  const open = Boolean(anchorEl)
  const id = open ? 'upload-tracker-popover' : undefined
  const hasInterruptedUploads = interruptedUploads > 0
  const hasPausedUploads = pausedUploads > 0

  // Determine icon color based on upload states
  const getIconColor = () => {
    if (activeUploads > 0) return 'primary'
    if (hasInterruptedUploads) return 'warning'
    if (hasPausedUploads) return 'info'
    return 'default'
  }

  const handleClearInterrupted = () => {
    uploadService.clearInterruptedUploads()
    setIsClearInterruptedDialogOpen(false)
    enqueueSnackbar('Interrupted uploads cleared', { variant: 'info' })
  }

  return (
    <>
      <Tooltip title={hasInterruptedUploads ? 'You have interrupted uploads to resume' : 'Upload Progress'}>
        <IconContainer>
          {hasInterruptedUploads && <WarningIndicator />}
          <IconButton onClick={handleClick} size="medium" color={getIconColor()}>
            <StyledBadge badgeContent={activeUploads + pausedUploads} color="primary" max={99}>
              <CloudUploadIcon />
            </StyledBadge>
          </IconButton>
        </IconContainer>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 2,
          sx: {
            borderRadius: 1.5,
            overflow: 'hidden',
            width: 420,
            maxHeight: 600,
          },
        }}
      >
        {totalUploads > 0 ? (
          <>
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                Uploads
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {activeUploads > 0 && (
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', mr: 0.5 }} />
                    {activeUploads} active
                  </Typography>
                )}
                {pausedUploads > 0 && (
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'info.main' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'info.main', mr: 0.5 }} />
                    {pausedUploads} paused
                  </Typography>
                )}
                {interruptedUploads > 0 && (
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', mr: 0.5 }} />
                    {interruptedUploads} interrupted
                  </Typography>
                )}
                {completedUploads > 0 && (
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', mr: 0.5 }} />
                    {completedUploads} completed
                  </Typography>
                )}
                {failedUploads > 0 && (
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', mr: 0.5 }} />
                    {failedUploads} failed
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  px: 1,
                  minHeight: 40,
                  '& .MuiTabs-flexContainer': {
                    gap: 0.5,
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                  },
                }}
              >
                <StyledTab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="caption">All</Typography>
                    </Box>
                  }
                />
                <StyledTab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TimerIcon sx={{ fontSize: 12, mr: 0.5 }} />
                      <Typography variant="caption">Active/Paused ({activeUploads + pausedUploads})</Typography>
                    </Box>
                  }
                  disabled={activeUploads === 0 && pausedUploads === 0}
                />
                <StyledTab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <WarningIcon sx={{ fontSize: 12, mr: 0.5 }} />
                      <Typography variant="caption">Interrupted ({interruptedUploads})</Typography>
                    </Box>
                  }
                  disabled={interruptedUploads === 0}
                  sx={{ color: interruptedUploads > 0 ? 'warning.main' : 'inherit' }}
                />
              </Tabs>
            </Box>

            {hasInterruptedUploads && tabValue === TAB_ALL && (
              <Box sx={{ px: 2, pt: 1, pb: 0 }}>
                <Typography
                  variant="caption"
                  sx={{ color: 'warning.dark', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <InfoIcon sx={{ fontSize: 14 }} /> Tip: Use the <b>Clear</b> button to remove all interrupted uploads.
                </Typography>
              </Box>
            )}

            {hasInterruptedUploads && tabValue === TAB_INTERRUPTED && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
                    borderLeft: '4px solid',
                    borderColor: 'warning.main',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    mt: 1,
                    mb: 1,
                    boxShadow: 'none',
                  }}
                >
                  <WarningIcon sx={{ color: 'warning.main', fontSize: 22, mr: 1 }} />
                  <Typography variant="body2" sx={{ color: 'text.primary', flex: 1 }}>
                    Some uploads were interrupted. You can resume or clear them.
                  </Typography>
                  {interruptedUploadsArray.length > 0 && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<CancelIcon sx={{ fontSize: 18 }} />}
                      onClick={() => setIsClearInterruptedDialogOpen(true)}
                      sx={{
                        bgcolor: 'warning.main',
                        color: 'warning.contrastText',
                        fontWeight: 600,
                        borderRadius: 1,
                        px: 2,
                        py: 0.5,
                        minWidth: 0,
                        boxShadow: 'none',
                        textTransform: 'none',
                        '&:hover': {
                          bgcolor: 'warning.dark',
                          color: 'common.white',
                        },
                      }}
                      aria-label="Clear interrupted uploads"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') setIsClearInterruptedDialogOpen(true)
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </Box>
                {/* Confirmation Dialog */}
                <Dialog
                  open={isClearInterruptedDialogOpen}
                  onClose={() => setIsClearInterruptedDialogOpen(false)}
                  maxWidth="xs"
                  fullWidth
                  aria-labelledby="clear-interrupted-dialog-title"
                >
                  <DialogTitle id="clear-interrupted-dialog-title">Clear Interrupted Uploads?</DialogTitle>
                  <DialogContent>
                    <Typography>
                      This will remove all interrupted uploads from the upload tracker. This action cannot be undone.
                    </Typography>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setIsClearInterruptedDialogOpen(false)} color="inherit">
                      Cancel
                    </Button>
                    <Button onClick={handleClearInterrupted} color="warning" variant="contained" autoFocus>
                      Clear Interrupted
                    </Button>
                  </DialogActions>
                </Dialog>
              </>
            )}

            {hasPausedUploads && tabValue === TAB_ACTIVE && (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: (theme) => (theme.palette.mode === 'light' ? 'info.dark' : 'info.light'),
                  }}
                >
                  <InfoIcon sx={{ fontSize: 14, mr: 0.5 }} />
                  Click <PlayArrowIcon sx={{ fontSize: 12, mx: 0.3 }} /> to resume paused uploads
                </Typography>
              </Box>
            )}

            <List disablePadding sx={{ p: 1, overflow: 'auto', maxHeight: 300 }}>
              {filteredUploads.length > 0 ? (
                filteredUploads.map((upload) => {
                  let UploadItemComponent

                  // Determine which styled component to use based on upload status
                  switch (upload.status) {
                    case 'interrupted':
                      UploadItemComponent = InterruptedUploadItem
                      break
                    case 'paused':
                      UploadItemComponent = PausedUploadItem
                      break
                    case 'completed':
                      UploadItemComponent = CompletedUploadItem
                      break
                    case 'failed':
                      UploadItemComponent = FailedUploadItem
                      break
                    default:
                      UploadItemComponent = ActiveUploadItem
                  }

                  const isInterrupted = upload.status === 'interrupted'
                  const isActive = upload.status === 'uploading'
                  const isPaused = upload.status === 'paused'

                  return (
                    <UploadItemComponent key={upload.id}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            maxWidth: '75%',
                            fontWeight: 'medium',
                            color: isInterrupted ? 'warning.dark' : isPaused ? 'info.dark' : 'inherit',
                          }}
                        >
                          {upload.filename}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {isActive && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handlePause(upload.id)}
                                aria-label="pause upload"
                                color="info"
                              >
                                <PauseIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleCancel(upload.id)}
                                aria-label="cancel upload"
                              >
                                <CancelIcon fontSize="small" color="action" />
                              </IconButton>
                            </>
                          )}
                          {isPaused && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleResumePaused(upload.id)}
                                aria-label="resume upload"
                                color="primary"
                                sx={{
                                  animation: 'fadeIn 1s ease',
                                  '@keyframes fadeIn': {
                                    '0%': { opacity: 0.6 },
                                    '100%': { opacity: 1 },
                                  },
                                }}
                              >
                                <PlayArrowIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleCancel(upload.id)}
                                aria-label="cancel upload"
                              >
                                <CancelIcon fontSize="small" color="action" />
                              </IconButton>
                            </>
                          )}
                          {isInterrupted && (
                            <IconButton
                              size="small"
                              onClick={() => handleResumeUpload(upload)}
                              aria-label="resume upload"
                              color="warning"
                              sx={{
                                animation: 'fadeIn 1s ease',
                                '@keyframes fadeIn': {
                                  '0%': { opacity: 0.6 },
                                  '100%': { opacity: 1 },
                                },
                              }}
                            >
                              <RestartAltIcon fontSize="small" />
                            </IconButton>
                          )}
                          {(upload.status === 'completed' ||
                            upload.status === 'failed' ||
                            upload.status === 'cancelled') && (
                            <IconButton
                              size="small"
                              onClick={() => handleRemove(upload.id)}
                              aria-label="remove from list"
                            >
                              <CancelIcon fontSize="small" color="action" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>

                      {isActive && <ProgressBar value={parseFloat(upload.progress)} />}

                      {isInterrupted && <InterruptedProgressBar value={parseFloat(upload.progress)} />}

                      {isPaused && <PausedProgressBar value={parseFloat(upload.progress)} />}

                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        {upload.status === 'uploading' && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              justifyContent: 'space-between',
                            }}
                          >
                            <span>
                              {formatFileSize(upload.totalBytes * (upload.progress / 100))} /{' '}
                              {formatFileSize(upload.totalBytes)}
                            </span>
                            <span>{formatTimeRemaining(upload.timeRemaining)}</span>
                          </Typography>
                        )}

                        {isInterrupted && (
                          <Typography
                            variant="caption"
                            color="warning.main"
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            <WarningIcon fontSize="small" sx={{ mr: 0.5, fontSize: 12 }} />
                            Interrupted ({upload.progress}%)
                          </Typography>
                        )}

                        {isPaused && (
                          <Typography
                            variant="caption"
                            color="info.main"
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            <PauseIcon fontSize="small" sx={{ mr: 0.5, fontSize: 12 }} />
                            Paused ({upload.progress}%)
                          </Typography>
                        )}

                        {upload.status === 'completed' && (
                          <Typography
                            variant="caption"
                            color="success.main"
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            <CheckCircleIcon fontSize="small" sx={{ mr: 0.5, fontSize: 12 }} />
                            Complete
                          </Typography>
                        )}

                        {upload.status === 'failed' && (
                          <Typography
                            variant="caption"
                            color="error.main"
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            <ErrorIcon fontSize="small" sx={{ mr: 0.5, fontSize: 12 }} />
                            Failed
                          </Typography>
                        )}

                        {upload.status === 'cancelled' && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            <CancelIcon fontSize="small" sx={{ mr: 0.5, fontSize: 12 }} />
                            Cancelled
                          </Typography>
                        )}
                      </Box>
                    </UploadItemComponent>
                  )
                })
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="text.secondary" variant="caption">
                    {tabValue === TAB_ALL ? 'No uploads' : 'No uploads in this category'}
                  </Typography>
                </Box>
              )}
            </List>
          </>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">
              No recent uploads
            </Typography>
          </Box>
        )}
      </Popover>

      {/* File Mismatch Warning Dialog */}
      <Dialog
        open={mismatchDialog.open}
        onClose={handleCancelMismatch}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          elevation: 2,
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
            <WarningIcon sx={{ mr: 1 }} />
            File Mismatch
          </Box>
        </DialogTitle>
        <DialogContent>
          {mismatchDialog.details && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                The file you selected does not match the original interrupted upload:
              </Typography>

              <Box sx={{ backgroundColor: 'background.neutral', p: 1.5, borderRadius: 1, mb: 2 }}>
                <Typography variant="caption" sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <span style={{ fontWeight: 'bold' }}>Original file:</span>
                  <span>{mismatchDialog.details.details.original.name}</span>
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 'bold' }}>Original size:</span>
                  <span>{formatFileSize(mismatchDialog.details.details.original.size)}</span>
                </Typography>

                <Divider sx={{ my: 1 }} />

                <Typography variant="caption" sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <span style={{ fontWeight: 'bold' }}>Selected file:</span>
                  <span>{mismatchDialog.details.details.current.name}</span>
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 'bold' }}>Selected size:</span>
                  <span>{formatFileSize(mismatchDialog.details.details.current.size)}</span>
                </Typography>
              </Box>

              <Typography variant="body2" color="warning.dark">
                Resuming with a different file may cause upload errors or data corruption.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelMismatch} color="primary">
            Cancel
          </Button>
          <Button onClick={handleForceProceed} variant="contained" color="warning" startIcon={<WarningIcon />}>
            Upload Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default UploadTracker
